import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Skeleton,
  Modal,
  Input,
  Select,
  FileUpload,
  ErrorBoundary,
  Timeline,
  useToast,
} from '@bbpos/ui';
import type { TimelineItem } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { GLASS, GLASS_FORM } from '@/constants/design-tokens';
import {
  useSalesOrder,
  useRecordSalesPayment,
  useAddSalesOrderDocument,
  useDeleteSalesOrderDocument,
  useCancelSalesOrder,
  useDownloadProformaPdf,
  useDownloadSalesInvoicePdf,
  SalesOrderStatusBadge,
  SalesInvoiceStatusBadge,
  lineFulfilment,
  FULFILMENT_LABELS,
  formatQty,
} from '@/features/sales-orders';
import type { SalesOrderInvoice, SalesOrderStatus, SalesPaymentMethod, SalesOrderDocumentKind, LineFulfilmentStatus } from '@/features/sales-orders';
import { QuotationHero } from '@/features/quotations';
import { uploadPurchaseDocument, DocumentUploadError, DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES, formatBytes } from '@/features/vendor-bills';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { toLocalIsoDate } from '../purchase-orders/date-utils';
import { TruckIcon, DownloadIcon, BanIcon, TrashIcon } from '../purchase-orders/icons';
import { FileTextIcon, CurrencyIcon } from '../quotations/icons';

// ─── Status accent config (left-rail tone tile — no borders) ────────────────

const STATUS_ACCENT: Record<SalesOrderStatus, { tint: string; rail: string; description: string }> = {
  CONFIRMED: {
    tint: 'bg-brand-50/80 dark:bg-brand-900/20',
    rail: 'before:bg-brand-500',
    description: 'Customer PO recorded. Nothing has shipped yet — record deliveries as goods leave.',
  },
  PARTIALLY_DELIVERED: {
    tint: 'bg-warning-50/80 dark:bg-warning-900/20',
    rail: 'before:bg-warning-500',
    description: 'Some lines are still to be delivered. Invoice what has shipped or the whole order.',
  },
  DELIVERED: {
    tint: 'bg-violet-50/80 dark:bg-violet-900/20',
    rail: 'before:bg-violet-500',
    description: 'Everything has shipped. Completes once fully invoiced and paid.',
  },
  COMPLETED: {
    tint: 'bg-success-50/80 dark:bg-success-900/20',
    rail: 'before:bg-success-500',
    description: 'Delivered, invoiced and paid in full.',
  },
  CANCELLED: {
    tint: 'bg-neutral-50/80 dark:bg-neutral-800/40',
    rail: 'before:bg-neutral-400',
    description: 'This order was cancelled before any activity.',
  },
};

const STATUS_RAIL_BASE =
  "relative pl-5 before:content-[''] before:absolute before:left-0 before:top-3 before:bottom-3 before:w-1 before:rounded-full";

const FULFILMENT_PILL: Record<LineFulfilmentStatus, string> = {
  PENDING: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800/60 dark:text-neutral-300',
  PARTIAL: 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300',
  DELIVERED: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  COMPLETE: 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300',
};

const METHOD_OPTIONS: { value: SalesPaymentMethod; label: string }[] = [
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CARD', label: 'Card' },
];

const DOC_KIND_OPTIONS: { value: SalesOrderDocumentKind; label: string }[] = [
  { value: 'CUSTOMER_PO', label: 'Customer PO' },
  { value: 'OTHER', label: 'Other' },
];

function fmtDate(v: string | null | undefined): string {
  return v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

function methodLabel(m: string): string {
  return METHOD_OPTIONS.find((o) => o.value === m)?.label ?? m;
}

// Hand a streamed PDF to the browser as a download (filename from Content-Disposition).
function saveBlob({ blob, filename }: { blob: Blob; filename?: string }, fallback: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? fallback;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Content ────────────────────────────────────────────────────────────────

function SalesOrderDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const tax = useTaxRegime();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const dp = getCurrencyDecimals(currency);
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isManager = roleLevel >= ROLE_LEVELS.MANAGER;

  const { data: so, isLoading, error } = useSalesOrder(id);
  const payMutation = useRecordSalesPayment();
  const addDocMutation = useAddSalesOrderDocument();
  const deleteDocMutation = useDeleteSalesOrderDocument();
  const cancelMutation = useCancelSalesOrder();
  const proformaMutation = useDownloadProformaPdf();
  const invoicePdfMutation = useDownloadSalesInvoicePdf();

  const [showPay, setShowPay] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<SalesPaymentMethod>('BANK_TRANSFER');
  const [payDate, setPayDate] = useState(toLocalIsoDate(new Date()));
  const [payRef, setPayRef] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [docKind, setDocKind] = useState<SalesOrderDocumentKind>('CUSTOMER_PO');
  const [uploadingDoc, setUploadingDoc] = useState(false);

  if (error) {
    toast({ title: (error as Error).message, variant: 'error' });
    navigate('/sales-orders');
    return null;
  }
  if (isLoading || !so) {
    return (
      <>
        <Skeleton variant="rect" height={104} className="rounded-2xl mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton variant="rect" height={200} className="rounded-2xl" />
            <Skeleton variant="rect" height={320} className="rounded-2xl" />
          </div>
          <Skeleton variant="rect" height={240} className="rounded-2xl" />
        </div>
      </>
    );
  }

  const lines = so.items.map((item) => ({ item, f: lineFulfilment(item) }));
  const isOpen = so.status !== 'COMPLETED' && so.status !== 'CANCELLED';
  const canDeliver = isManager && (so.status === 'CONFIRMED' || so.status === 'PARTIALLY_DELIVERED') && lines.some((l) => l.f.remaining_to_deliver > 0);
  const canInvoice = isManager && isOpen && lines.some((l) => l.f.remaining_to_invoice > 0);
  const canPay = isManager && so.status !== 'CANCELLED' && so.paid_total < so.grand_total;
  const canProforma = so.status !== 'CANCELLED';
  const canUpload = isManager && so.status !== 'CANCELLED';
  const canCancel = isManager && so.status === 'CONFIRMED';
  // Payments against an invoice (and cancelling one) live in Finance → Sales Invoices; the order only takes advances.
  const advanceCap = so.grand_total - so.paid_total;
  const accent = STATUS_ACCENT[so.status] ?? STATUS_ACCENT.CONFIRMED;

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleDownloadProforma() {
    proformaMutation.mutate(so!.id, {
      onSuccess: (r) => { saveBlob(r, `PI-${so!.so_number}.pdf`); toast({ title: `PI-${so!.so_number}.pdf downloaded`, variant: 'success' }); },
      onError: (e: Error) => toast({ title: e.message || 'Failed to download the pro-forma', variant: 'error' }),
    });
  }

  function handleDownloadInvoice(inv: SalesOrderInvoice) {
    invoicePdfMutation.mutate({ id: so!.id, invoiceId: inv.id }, {
      onSuccess: (r) => saveBlob(r, `${inv.invoice_number}.pdf`),
      onError: (e: Error) => toast({ title: e.message || 'Failed to download the invoice', variant: 'error' }),
    });
  }

  function openAdvance() {
    setPayAmount((advanceCap / subunit).toFixed(dp));
    setPayMethod('BANK_TRANSFER');
    setPayDate(toLocalIsoDate(new Date()));
    setPayRef('');
    setPayNotes('');
    setShowPay(true);
  }

  function handleAdvance() {
    const amount = Math.round((parseFloat(payAmount) || 0) * subunit);
    if (amount <= 0 || amount > advanceCap) {
      toast({ title: `Amount must be between ${formatCurrency(1, currency)} and ${formatCurrency(advanceCap, currency)}`, variant: 'error' });
      return;
    }
    payMutation.mutate(
      {
        id: so!.id,
        payload: {
          amount, payment_method: payMethod, paid_on: payDate || undefined, reference: payRef.trim() || undefined,
          notes: payNotes.trim() || undefined,
        },
      },
      {
        onSuccess: (p) => {
          setShowPay(false);
          toast({ title: `Advance of ${formatCurrency(p.amount, currency)} recorded — it settles the next invoice raised`, variant: 'success' });
        },
        onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
      },
    );
  }

  function handleCancelOrder() {
    cancelMutation.mutate({ id: so!.id, payload: { reason: cancelReason.trim() } }, {
      onSuccess: () => { setShowCancel(false); toast({ title: `${so!.so_number} cancelled`, variant: 'success' }); },
      onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
    });
  }

  async function handleUploadDocument(files: File[]) {
    const f = files[0];
    if (!f) return;
    setUploadingDoc(true);
    try {
      const payload = await uploadPurchaseDocument(f);
      addDocMutation.mutate({ id: so!.id, payload: { ...payload, kind: docKind } }, {
        onSuccess: () => toast({ title: `${f.name} attached`, variant: 'success' }),
        onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
      });
    } catch (err) {
      toast({ title: err instanceof DocumentUploadError ? err.message : 'Upload failed', variant: 'error' });
    } finally {
      setUploadingDoc(false);
    }
  }

  // ── Timeline ──────────────────────────────────────────────────────────────

  const timelineItems: TimelineItem[] = [
    {
      id: 'created', type: 'confirmed', timestamp: so.created_at,
      action: `Sales order confirmed from ${so.quotation.quote_number} · PO ${so.customer_po_number}`,
      ...(so.created_by_name ? { actor: so.created_by_name } : {}),
    },
  ];
  so.deliveries.forEach((d, idx) => {
    const units = d.items.reduce((s, i) => s + i.quantity, 0);
    const last = idx === so.deliveries.length - 1 && (so.status === 'DELIVERED' || so.status === 'COMPLETED');
    timelineItems.push({
      id: `dc-${d.id}`, type: last ? 'completed' : 'inProgress', timestamp: d.created_at ?? d.delivered_date,
      action: `${d.delivery_number}: delivered ${formatQty(units)} units${d.reference ? ` · ${d.reference}` : ''}${last ? ' — order fully delivered' : ''}`,
      ...(d.created_by_name ? { actor: d.created_by_name } : {}),
    });
  });
  for (const inv of so.invoices) {
    timelineItems.push({
      id: `si-${inv.id}`, type: inv.status === 'CANCELLED' ? 'cancelled' : inv.status === 'PAID' ? 'completed' : 'pending',
      timestamp: inv.created_at ?? inv.invoice_date,
      action: `Invoice ${inv.invoice_number} · ${formatCurrency(inv.total_amount, currency)} · ${inv.status.replace('_', ' ').toLowerCase()}`,
    });
  }
  for (const p of so.payments) {
    timelineItems.push({
      id: `pay-${p.id}`, type: 'paid', timestamp: p.paid_on,
      action: `Payment ${formatCurrency(p.amount, currency)} via ${methodLabel(p.payment_method)}${p.invoice_number ? ` · ${p.invoice_number}` : ' · advance'}`,
      ...(p.created_by_name ? { actor: p.created_by_name } : {}),
    });
  }
  if (so.status === 'CANCELLED') {
    timelineItems.push({
      id: 'cancelled', type: 'cancelled', timestamp: so.cancelled_at ?? so.updated_at,
      action: `Cancelled${so.cancel_reason ? `: ${so.cancel_reason}` : ''}`,
    });
  }
  timelineItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return (
    <>
      <QuotationHero
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <span className="font-mono tabular-nums">{so.so_number}</span>
            <SalesOrderStatusBadge status={so.status} size="md" />
          </span>
        }
        subtitle={[so.customer_name, `PO ${so.customer_po_number}`, so.expected_delivery_date ? `Expected ${fmtDate(so.expected_delivery_date)}` : null].filter(Boolean).join(' · ')}
        right={
          <div className="text-right">
            <p className="font-mono text-3xl font-bold tabular-nums">{formatCurrency(so.grand_total, currency)}</p>
            <p className="text-xs uppercase tracking-wide text-brand-100">Grand total · {formatCurrency(so.outstanding, currency)} outstanding</p>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order information */}
          <div className={`${GLASS} p-6 animate-slide-up`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">Order Information</h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Sales order</dt><dd className="font-mono font-medium tabular-nums">{so.so_number}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Created</dt><dd>{fmtDate(so.created_at)}{so.created_by_name ? ` · ${so.created_by_name}` : ''}</dd></div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Customer</dt>
                <dd>
                  <div className="font-medium text-neutral-800 dark:text-neutral-200">{so.customer_name}</div>
                  {so.customer_phone && <div className="text-xs text-neutral-500 tabular-nums">{so.customer_phone}</div>}
                  {so.customer_gstin && <div className="text-xs text-neutral-500 font-mono">{tax.taxIdLabel} {so.customer_gstin}</div>}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">From quotation</dt>
                <dd>
                  <button type="button" onClick={() => navigate(`/quotations/${so.quotation.id}`)} className="font-mono text-brand-600 dark:text-brand-400 hover:underline">
                    {so.quotation.quote_number}
                  </button>
                </dd>
              </div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Customer PO</dt><dd className="font-mono">{so.customer_po_number}{so.customer_po_date ? <span className="text-neutral-500 font-sans"> · {fmtDate(so.customer_po_date)}</span> : null}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Expected delivery</dt><dd>{fmtDate(so.expected_delivery_date)}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Location</dt><dd>{so.location_name ?? '—'}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Billing address</dt><dd className="whitespace-pre-wrap">{so.billing_address ?? '—'}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Shipping address</dt><dd className="whitespace-pre-wrap">{so.shipping_address ?? '—'}</dd></div>
              {so.notes && <div className="sm:col-span-2"><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Notes</dt><dd className="whitespace-pre-wrap">{so.notes}</dd></div>}
              {so.cancel_reason && <div className="sm:col-span-2"><dt className="text-xs uppercase tracking-wide text-danger-600 mb-1">Cancelled</dt><dd>{so.cancel_reason} · {fmtDate(so.cancelled_at)}</dd></div>}
            </dl>
          </div>

          {/* Lines */}
          <div className={`${GLASS} p-6 animate-slide-up`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">Lines ({so.items.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 dark:border-neutral-800">
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Product</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Ordered</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Unit price</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">{tax.taxName}</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Line total</th>
                    <th className="text-left py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Fulfilment</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(({ item, f }) => {
                    const pct = f.ordered > 0 ? Math.min(100, (f.delivered / f.ordered) * 100) : 0;
                    return (
                      <tr key={item.id} className="border-b border-neutral-50 dark:border-neutral-800/50 last:border-0 align-top even:bg-neutral-50/60 dark:even:bg-neutral-800/30 hover:bg-brand-50/60 dark:hover:bg-brand-900/10 transition-colors">
                        <td className="py-2.5 pr-3">
                          <div className="font-medium text-neutral-800 dark:text-neutral-200">{item.product_name}</div>
                          <div className="text-[11px] text-neutral-500 font-mono">{item.product_sku}{item.hsn_code ? ` · HSN ${item.hsn_code}` : ''}</div>
                        </td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{formatQty(item.quantity)}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{formatCurrency(item.unit_price, currency)}</td>
                        <td className="py-2.5 pr-3 text-right tabular-nums">{item.gst_rate}%</td>
                        <td className="py-2.5 pr-3 text-right font-semibold tabular-nums">{formatCurrency(item.line_total, currency)}</td>
                        <td className="py-2.5 min-w-[11rem]">
                          <div className="flex h-2 rounded-full bg-neutral-200/60 dark:bg-neutral-700/60 overflow-hidden">
                            <div className={`${f.delivered >= f.ordered ? 'bg-success-500' : 'bg-teal-500'} transition-all duration-300`} style={{ width: `${pct}%` }} />
                          </div>
                          <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-neutral-500 dark:text-neutral-400 tabular-nums">
                            <span>{formatQty(f.delivered)}/{formatQty(f.ordered)} delivered · {formatQty(f.invoiced)} invoiced</span>
                            <span className={`rounded-full px-2 py-0.5 font-semibold ${FULFILMENT_PILL[f.status]}`}>{FULFILMENT_LABELS[f.status]}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-1.5 text-sm">
              <div className="space-y-1.5">
                <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span className="tabular-nums">{formatCurrency(so.subtotal, currency)}</span></div>
                {so.discount_total > 0 && <div className="flex justify-between"><span className="text-neutral-500">Discount</span><span className="tabular-nums text-success-600">−{formatCurrency(so.discount_total, currency)}</span></div>}
                <div className="flex justify-between"><span className="text-neutral-500">Tax</span><span className="tabular-nums">{formatCurrency(so.tax_total, currency)}</span></div>
                <div className="flex justify-between font-bold pt-2 border-t border-neutral-100 dark:border-neutral-800"><span>Grand total</span><span className="tabular-nums">{formatCurrency(so.grand_total, currency)}</span></div>
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between"><span className="text-neutral-500">Delivered value</span><span className="tabular-nums">{formatCurrency(so.delivered_value, currency)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Invoiced</span><span className="tabular-nums">{formatCurrency(so.invoiced_total, currency)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Received{so.advance_total > 0 ? ` (incl. ${formatCurrency(so.advance_total, currency)} advance)` : ''}</span><span className="tabular-nums text-success-600">{formatCurrency(so.paid_total, currency)}</span></div>
                {/* Advance not yet adjusted against an invoice — it will settle the next one raised. */}
                {so.advance_unapplied > 0 && <div className="flex justify-between"><span className="text-neutral-500">Advance held (not yet invoiced)</span><span className="tabular-nums text-violet-600 dark:text-violet-300">{formatCurrency(so.advance_unapplied, currency)}</span></div>}
                <div className="flex justify-between font-semibold pt-2 border-t border-neutral-100 dark:border-neutral-800"><span>Outstanding</span><span className={`tabular-nums ${so.outstanding > 0 ? 'text-warning-700 dark:text-warning-300' : ''}`}>{formatCurrency(so.outstanding, currency)}</span></div>
              </div>
            </div>
          </div>

          {/* Deliveries */}
          {so.deliveries.length > 0 && (
            <div className={`${GLASS} p-6 animate-slide-up`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Deliveries ({so.deliveries.length})</h3>
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {so.deliveries.map((d) => (
                  <li key={d.id} className="py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <div>
                        <span className="font-mono font-semibold text-neutral-800 dark:text-neutral-200">{d.delivery_number}</span>
                        <span className="text-neutral-500"> · {fmtDate(d.delivered_date)}{d.created_by_name ? ` · ${d.created_by_name}` : ''}</span>
                        {d.reference && <span className="ml-2 inline-flex rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 text-xs text-indigo-700 dark:text-indigo-200">{d.reference}</span>}
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                      {d.items.map((i) => `${i.item_name ?? ''} × ${formatQty(i.quantity)}`).join(' · ')}
                    </div>
                    {d.notes && <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{d.notes}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Invoices */}
          {so.invoices.length > 0 && (
            <div className={`${GLASS} p-6 animate-slide-up`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Invoices ({so.invoices.length})</h3>
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {so.invoices.map((inv) => (
                  <li key={inv.id} className="flex flex-wrap items-center justify-between gap-3 py-2.5 text-sm">
                    <div className="min-w-0">
                      {/* Payments and cancellation live on the invoice itself (Finance → Sales Invoices). */}
                      {isManager ? (
                        <button type="button" onClick={() => navigate(`/sales-invoices/${inv.id}`)} className="font-mono font-medium text-brand-600 dark:text-brand-400 hover:underline">{inv.invoice_number}</button>
                      ) : (
                        <span className="font-mono font-medium text-brand-600 dark:text-brand-400">{inv.invoice_number}</span>
                      )}
                      <span className="text-neutral-500 text-xs"> · {fmtDate(inv.invoice_date)}{inv.due_date ? ` · due ${fmtDate(inv.due_date)}` : ''}</span>
                    </div>
                    <SalesInvoiceStatusBadge status={inv.status} />
                    <span className="tabular-nums">
                      <span className="font-semibold">{formatCurrency(inv.total_amount, currency)}</span>
                      {inv.status !== 'CANCELLED' && inv.balance_due > 0 && <span className="text-xs text-neutral-500"> · {formatCurrency(inv.balance_due, currency)} due</span>}
                      {inv.status !== 'CANCELLED' && inv.advance_applied > 0 && <span className="text-xs text-violet-600 dark:text-violet-300"> · {formatCurrency(inv.advance_applied, currency)} advance applied</span>}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        title="Download PDF"
                        aria-label={`Download ${inv.invoice_number}`}
                        onClick={() => handleDownloadInvoice(inv)}
                        className="p-1.5 rounded-lg text-neutral-500 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors"
                      >
                        <DownloadIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Payments */}
          {so.payments.length > 0 && (
            <div className={`${GLASS} p-6 animate-slide-up`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Payments ({so.payments.length})</h3>
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {so.payments.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
                    <span className="tabular-nums text-neutral-600 dark:text-neutral-400">{fmtDate(p.paid_on)}</span>
                    <span className="text-neutral-700 dark:text-neutral-300">{methodLabel(p.payment_method)}{p.reference ? ` · ${p.reference}` : ''}</span>
                    {p.invoice_number ? (
                      <span className="font-mono text-xs text-neutral-500">{p.invoice_number}</span>
                    ) : (
                      <span className="rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-2 py-0.5 text-[11px] font-semibold">Advance</span>
                    )}
                    <span className="text-xs text-neutral-500">{p.created_by_name ?? ''}</span>
                    <span className="font-semibold tabular-nums">{formatCurrency(p.amount, currency)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Documents */}
          <div className={`${GLASS} p-6 animate-slide-up`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Documents ({so.documents.length})</h3>
              {canUpload && (
                <div className="flex items-center gap-2">
                  <div className="w-40">
                    <Select options={DOC_KIND_OPTIONS} value={docKind} onChange={(v) => setDocKind((typeof v === 'string' ? v : 'CUSTOMER_PO') as SalesOrderDocumentKind)} />
                  </div>
                  <div className="w-44">
                    <FileUpload variant="button" accept={DOCUMENT_ACCEPT} maxSize={DOCUMENT_MAX_BYTES} loading={uploadingDoc} onUpload={(files: File[]) => { void handleUploadDocument(files); }} />
                  </div>
                </div>
              )}
            </div>
            {so.documents.length === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">No customer PO or other documents attached yet.</p>
            ) : (
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {so.documents.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium truncate text-neutral-800 dark:text-neutral-200">{d.file_name ?? d.file_key.split('/').pop()}</div>
                      <div className="text-xs text-neutral-500">{d.kind === 'CUSTOMER_PO' ? 'customer PO' : 'other'} · {formatBytes(d.size_bytes)} · {d.uploaded_by_name ?? ''} · {fmtDate(d.created_at)}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand-600 hover:underline"><DownloadIcon className="h-4 w-4" />View</a>}
                      {isManager && (
                        <button
                          type="button"
                          title="Remove"
                          aria-label={`Remove ${d.file_name ?? 'document'}`}
                          onClick={() => deleteDocMutation.mutate({ id: so.id, docId: d.id }, { onSuccess: () => toast({ title: 'Document removed', variant: 'success' }), onError: (e: Error) => toast({ title: e.message, variant: 'error' }) })}
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Timeline */}
          <div className={`${GLASS} p-6 animate-slide-up`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">Activity</h3>
            <Timeline items={timelineItems} />
          </div>
        </div>

        {/* Right rail */}
        <div className="space-y-4">
          <div className={`${GLASS_FORM} ${STATUS_RAIL_BASE} ${accent.rail} ${accent.tint} p-5 animate-slide-up`}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">Status</h3>
            <div className="mb-2"><SalesOrderStatusBadge status={so.status} size="md" /></div>
            <p className="text-sm text-neutral-600 dark:text-neutral-300">{accent.description}</p>
          </div>

          {(canDeliver || canInvoice || canPay || canProforma || canCancel) && (
            <div className={`${GLASS_FORM} p-5 animate-slide-up`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Actions</h3>
              <div className="space-y-2">
                {canDeliver && (
                  <Button variant="primary" className="w-full" onClick={() => navigate(`/sales-orders/${so.id}/deliver`)}>
                    <TruckIcon className="h-4 w-4 mr-1.5" />
                    {so.deliveries.length > 0 ? 'Deliver more' : 'Record delivery'}
                  </Button>
                )}
                {canInvoice && (
                  <Button variant="primary" className="w-full" onClick={() => navigate(`/sales-orders/${so.id}/invoice`)}>
                    <FileTextIcon className="h-4 w-4 mr-1.5" />
                    Raise invoice
                  </Button>
                )}
                {canPay && (
                  <Button variant="secondary" className="w-full" onClick={openAdvance}>
                    <CurrencyIcon className="h-4 w-4 mr-1.5" />
                    Record advance
                  </Button>
                )}
                {canProforma && (
                  <Button variant="secondary" className="w-full" onClick={handleDownloadProforma} loading={proformaMutation.isPending}>
                    <DownloadIcon className="h-4 w-4 mr-1.5" />
                    Download pro-forma
                  </Button>
                )}
                {canCancel && (
                  <Button variant="danger" className="w-full" onClick={() => { setCancelReason(''); setShowCancel(true); }}>
                    <BanIcon className="h-4 w-4 mr-1.5" />
                    Cancel order
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Record advance — payments against an invoice are recorded from Finance → Sales Invoices */}
      <Modal
        open={showPay}
        onClose={() => setShowPay(false)}
        title={`Record advance · ${so.so_number}`}
        allowOverflow
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowPay(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAdvance} loading={payMutation.isPending}>Save advance</Button>
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Amount ({symbol})</label>
            <Input type="number" value={payAmount} onChange={setPayAmount} />
            <p className="text-xs text-neutral-500 mt-1">Receivable {formatCurrency(advanceCap, currency)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Method</label>
            <Select options={METHOD_OPTIONS} value={payMethod} onChange={(v) => setPayMethod((typeof v === 'string' ? v : 'BANK_TRANSFER') as SalesPaymentMethod)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Paid on</label>
            <Input type="date" value={payDate} onChange={setPayDate} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Reference</label>
            <Input value={payRef} onChange={setPayRef} placeholder="UTR / cheque no." />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Notes</label>
            <Input value={payNotes} onChange={setPayNotes} placeholder="Optional" />
          </div>
          <p className="col-span-2 text-xs text-neutral-500">
            The advance settles the next invoice raised on this order. To pay a specific invoice, open it under Finance → Sales Invoices.
          </p>
        </div>
      </Modal>

      {/* Cancel order */}
      <Modal
        open={showCancel}
        onClose={() => setShowCancel(false)}
        title={`Cancel ${so.so_number}`}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowCancel(false)}>Keep order</Button>
            <Button variant="danger" disabled={!cancelReason.trim()} loading={cancelMutation.isPending} onClick={handleCancelOrder}>Cancel order</Button>
          </div>
        }
      >
        <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-3">
          Only an order with no deliveries, invoices or payments can be cancelled. The quotation stays converted. This cannot be undone.
        </p>
        <label className="block text-sm font-medium mb-1">Reason</label>
        <Input value={cancelReason} onChange={setCancelReason} placeholder="Why is this order being cancelled?" />
      </Modal>
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function SalesOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <PageContainer
      title="Sales Order"
      breadcrumbs={[
        { label: 'Sales Orders', href: '/sales-orders' },
        { label: id?.slice(0, 8).toUpperCase() ?? '' },
      ]}
    >
      <ErrorBoundary>
        <SalesOrderDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
